import type { Theme } from '@mui/material/styles';
import { getStatusChipColor } from 'app/utils/statusChipColors';

export type OnboardingStepDisplayState = 'completed' | 'pending' | 'disabled';

function parseToRgb(color: string): { r: number; g: number; b: number } {
  const trimmed = color.trim();
  const rgbMatch = trimmed.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    };
  }

  const hex = trimmed.replace(/^#/, '');
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }

  const match = hex.match(/(.{2})(.{2})(.{2})/);
  if (match) {
    return {
      r: parseInt(match[1], 16),
      g: parseInt(match[2], 16),
      b: parseInt(match[3], 16),
    };
  }

  return { r: 0, g: 0, b: 0 };
}

function interpolateColor(color1: string, color2: string, ratio: number): string {
  const start = parseToRgb(color1);
  const end = parseToRgb(color2);
  const r = Math.round(start.r + (end.r - start.r) * ratio);
  const g = Math.round(start.g + (end.g - start.g) * ratio);
  const b = Math.round(start.b + (end.b - start.b) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

/** YELLOW at 1% → SUCCESS (green) at 100%. Returns null at 0%. */
export function getOnboardingProgressColor(
  theme: Theme,
  percent: number,
): string | null {
  const rounded = Math.round(percent);
  if (rounded <= 0) {
    return null;
  }
  if (rounded >= 100) {
    return getStatusChipColor(theme, 'SUCCESS');
  }

  const startColor = getStatusChipColor(theme, 'YELLOW');
  const endColor = getStatusChipColor(theme, 'SUCCESS');
  const ratio = (rounded - 1) / 99;
  return interpolateColor(startColor, endColor, ratio);
}

export function resolveOnboardingStepDisplayState({
  completed,
  isAccessible,
}: {
  completed: boolean;
  isAccessible: boolean;
}): OnboardingStepDisplayState {
  if (completed) {
    return 'completed';
  }
  if (!isAccessible) {
    return 'disabled';
  }
  return 'pending';
}

export function getOnboardingPendingColor(theme: Theme): string {
  return theme.palette.text.primary;
}

export function getOnboardingStepTextColor(
  theme: Theme,
  state: OnboardingStepDisplayState,
): string {
  switch (state) {
    case 'completed':
      return getStatusChipColor(theme, 'SUCCESS');
    case 'disabled':
      return getStatusChipColor(theme, 'NO_STATUS');
    default:
      return getOnboardingPendingColor(theme);
  }
}

export function getOnboardingStepIconColor(
  theme: Theme,
  state: OnboardingStepDisplayState,
): string {
  return getOnboardingStepTextColor(theme, state);
}
